import toast from '@liquidweb-dev/undercurrent/toast';
import type { StripeError } from '@stripe/stripe-js';

import getPaymentMethodErrorMessage from 'modules/paymentMethod/getPaymentMethodErrorMessage';
import {
	getStripeErrorMessage,
	isStripeIntegrationFault,
	type PaymentFailureMessage,
} from 'modules/paymentMethod/stripeFailure';

const GENERIC: PaymentFailureMessage = {
	message: 'We couldn’t take your payment.',
	description: 'Please try again, or contact support if this keeps happening.',
};

/**
 * Deliberately not the decline copy. An integration fault is our bug, and
 * telling a customer their card was declined sends them to their bank and
 * support to the card issuer, for a problem neither can see.
 */
const INTEGRATION_FAULT: PaymentFailureMessage = {
	message: 'Something went wrong on our end.',
	description:
		'Nothing was charged. There is nothing wrong with the card — please try again, and contact support if this keeps happening.',
};

/**
 * Reports a failure from the steps *before* the capture - beginning a payment,
 * and authorizing the card against it. Nothing has been charged by either, so
 * the outcome is known and the customer stays on the form with what they
 * entered.
 *
 * Two kinds of error arrive here and only one is an `Error`: our API rejects
 * with an `ApiError`, and Stripe both throws a call it will not send and
 * returns a typed error when its own API refuses one. Only that returned shape
 * carries prose written for a customer, so anything that is a real `Error` is
 * read the way an API failure is.
 *
 * Returns nothing when the failure is the Element's own field validation - it
 * already shows that inline, and repeating it would show one problem twice.
 */
const reportPaymentFailure = (error: unknown): void => {
	// The customer is shown replacement copy, so this is the only place the actual
	// cause survives — and an integration fault is a bug of ours that nobody
	// would otherwise hear about.
	if (isStripeIntegrationFault(error)) {
		// eslint-disable-next-line no-console
		console.error('Stripe refused the payment:', error);
	}

	if (error instanceof Error) {
		toast.danger(getPaymentMethodErrorMessage(error));

		return;
	}

	const stripeMessage = getStripeErrorMessage(error as StripeError, {
		integrationFault: INTEGRATION_FAULT,
		generic: GENERIC,
	});

	if (stripeMessage) {
		toast.danger(stripeMessage.message, {
			description: stripeMessage.description,
		});
	}
};

export default reportPaymentFailure;
